import { BLOCK, SECTION } from '@/common/util/const';
import BLOCK_RESIZE_MAP from './block-resize-map';
import { createBlockSnippet } from '@/common/manager/setter-snippet';
const parseFlaten2Tree = (flaten = [], tree) => {
    flaten.forEach((item) => {
        if (!tree[item.groupIndex]) {
            tree[item.groupIndex] = [];
        }
        tree[item.groupIndex].push(item);
    });
};
/**
 * 一组（ span加和小于等于12 ）块，在遇到 resize 时候的处理行为
 * @param {*} groupArr 在原数组上进行修改
 * @param {*} blockNode 触发resize的节点
 * @param {*} offset 偏移量，-2表示左2格，1表示右1格
 * @returns
 */
const handleResize = (groupArr, blockNode, offset) => {
    const changeIndex = groupArr.findIndex((item) => item.id === blockNode.schema.id);
    const key = groupArr.map((item) => item.span).join(',');
    if (changeIndex < 0) {
        return;
    }
    const spans = key.split(',').map(Number);
    const afterGroupList = spans.map((numSpan, order) => {
        if (order === changeIndex) {
            return numSpan + offset;
        }
        if (order === changeIndex + 1) {
            return numSpan - offset;
        }
        return numSpan;
    });
    if (afterGroupList) {
        afterGroupList.forEach((col, i) => {
            groupArr[i].span = +col;
        });
    }
};
/**
 * 一组（ span加和小于等于12 ）块，在遇到切割时候的处理行为
 * @param {*} groupArr 在原数组上进行修改
 * @param {*} blockNode 触发 分割 的节点
 * @param {*} groupIndex 当前所处理的数组，他们有共同的组序列号
 * @returns
 */
const handleSplit = (groupArr, blockNode, groupIndex) => {
    const changeIndex = groupArr.findIndex((item) => item.id === blockNode.schema.id);
    const key = groupArr.map((item) => item.span).join(',');
    if (changeIndex < 0) {
        return;
    }
    const subKey = `${changeIndex}s`;
    const targetMap = BLOCK_RESIZE_MAP?.[key]?.[subKey];
    if (targetMap) {
        const originProps = blockNode.schema.props;
        const blockSnippet = createBlockSnippet({
            blockProps: originProps,
        });
        const newNode = blockNode?.document?.createNode(blockSnippet);
        newNode && blockNode.parent?.insertAfter(newNode, blockNode, false);
        const newNodeSchema = [blockNode, newNode].map((item) => {
            return {
                id: item?.id,
                groupIndex,
            };
        });
        groupArr.splice(changeIndex, 1, newNodeSchema[0], newNodeSchema[1]);
        targetMap.split(',').forEach((col, i) => {
            groupArr[i].span = +col;
        });
    }
};
/**
 * 一组（ span加和小于等于12 ）块，在遇到删除时候的处理行为
 * @param {*} afterTreeGroup 由于事件监听的是onNodeRemove, 所以对 delete 来说，这里已经是after的group了
 * @param {*} afterFlatenGroup 在原数组上进行修改
 * @param {*} blockNode 触发 删除 的节点
 * @param {*} sectionNode 触发 删除 的节点属的Block
 * @returns
 */
const handleDelete = (afterTreeGroup, afterFlatenGroup, blockNode, sectionNode) => {
    const treeMap = [];
    parseFlaten2Tree(sectionNode.lastFlatenMap, treeMap);
    treeMap.forEach((group) => {
        const { groupIndex } = group[0];
        const changeIndex = group.findIndex((item) => item.id === blockNode.schema.id);
        const key = group.map((item) => item.span).join(',');
        if (!afterTreeGroup?.[groupIndex]) {
            return;
        }
        if (changeIndex < 0) {
            afterFlatenGroup.push(...afterTreeGroup[groupIndex]);
            return;
        }
        const subKey = `${changeIndex}d`;
        const targetMap = BLOCK_RESIZE_MAP?.[key]?.[subKey];
        const targetArray = targetMap?.split(',');
        if (targetMap && afterTreeGroup[groupIndex]?.length >= targetArray.length) {
            targetArray.forEach((col, i) => {
                afterTreeGroup[groupIndex][i].span = +col;
            });
            afterFlatenGroup.push(...afterTreeGroup[groupIndex]);
        }
    });
};
// currentBlock.lastFlatenMap
const findGroupIndex = (id, map = []) => {
    let groupIndex = -1;
    map.forEach((m) => {
        if (m.id === id) {
            groupIndex = m.groupIndex;
        }
    });
    return groupIndex;
};
/**
 *
 * @param {*} currentSection parent refresh 的模式下，只要求有parent
 * @param {*} currentBlock child
 * @param {Enum} type 'split' | 'delete' | 'resize' | 'refresh'
 * @param {*} object 若是resize， offset表示移动的距离; node1, node2 表示新增之后的节点
 */
export const updateSpan = ({ parent: currentSection, child: currentBlock, type, offset = 0, }) => {
    if (!(currentSection &&
        currentSection.componentName === SECTION &&
        (type === 'refresh' || (currentBlock && currentBlock.componentName === BLOCK)))) {
        return;
    }
    const beforeFlatenGroup = [];
    const children = currentSection.schema.children || [];
    const childrenListSchema = Array.isArray(children) ? children : [children];
    if (!childrenListSchema) {
        // 把 SECTION 中的最后一个元素 BLOCK 删掉，意味着删掉 SECTION
        currentSection.remove();
        return;
    }
    let total = 0;
    let groupIndex = 0;
    childrenListSchema.forEach((c) => {
        const item = {
            id: c.id,
            span: c.props?.span || 12,
            groupIndex,
        };
        const inheritIndex = findGroupIndex(c.id, currentBlock?.lastFlatenMap);
        if (inheritIndex > -1 && type === 'delete') {
            item.groupIndex = inheritIndex;
        }
        else {
            total += c.props?.span || 0;
            if (total > 12) {
                groupIndex++;
                total = c.props?.span;
            }
            item.groupIndex = groupIndex;
        }
        beforeFlatenGroup.push(item);
    });
    // console.log(beforeFlatenGroup, '====== before group =====');
    const beforeTreeGroup = [];
    parseFlaten2Tree(beforeFlatenGroup, beforeTreeGroup);
    const afterFlatenGroup = [];
    if (type === 'resize' && offset !== 0) {
        beforeTreeGroup.forEach((group) => {
            handleResize(group, currentBlock, offset);
            afterFlatenGroup.push(...group);
        });
    }
    else if (type === 'split') {
        beforeTreeGroup.forEach((group, idx) => {
            handleSplit(group, currentBlock, idx);
            afterFlatenGroup.push(...group);
        });
    }
    else if (type === 'delete') {
        // 由于事件监听的是onNodeRemove, 所以对 delete 来说，这里已经是after的group了
        handleDelete(beforeTreeGroup, afterFlatenGroup, currentBlock, currentSection);
    }
    else {
        beforeTreeGroup.forEach((group) => {
            afterFlatenGroup.push(...group);
        });
    }
    afterFlatenGroup.forEach((data) => {
        const currentGroupIndex = data.groupIndex;
        const groupList = [];
        let num = 0;
        afterFlatenGroup.forEach((item) => {
            if (item.groupIndex === currentGroupIndex) {
                item.groupInnerIndex = num;
                item.span && groupList.push(item.span);
                num++;
            }
        });
        data.groupLength = groupList.length;
        data.groupList = groupList.join(',');
    });
    // console.log(afterFlatenGroup, '====== after group =====');
    const adjustColSpan = (afterFlaten) => {
        afterFlaten.forEach((item) => {
            item.id && currentSection.document?.getNodeById(item.id)?.setPropValue('span', item.span);
        });
    };
    adjustColSpan(afterFlatenGroup);
    currentSection.lastFlatenMap = afterFlatenGroup;
    // for delete
};
